import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import DateUtils from "../../../../../../../core/services/date-utils";
import {PriceMatrixTypes} from "../../../../../../components/dialogs/upsert-price-matrix";
import {ImportingDataTypes} from "../types";
import {DELIVERY_MODE_IMPORT_TITLES} from "../engine/shared/import-delivery-mode";
import {
    formatImportCodeUpperField,
    formatPartNumberImportField,
    formatSpreadsheetImportField,
} from "../engine/transformers/shared";
import {DEFAULT_PRODUCT_CODE_SPLITTER} from "../../../../../../components/app-specific/product-code-splitter";
import {codeGroupFieldKey, formatPartCodeImportField} from "../engine/shared/product-code-import-helpers";
import {parseDelimitedList} from "../engine/transformers/shared/inventory-availability-validation";

/**
 * @typedef {Object} BuildImportFieldsContext
 * @property {Array} [allPriceLevels]
 * @property {string} [lengthUnit]
 * @property {string} [weightUnit]
 * @property {Array} [allIndustries]
 * @property {Record<string, Array>} [technicalPropertiesOfIndustry]
 * @property {Array} [allPartCodeCategories]
 * @property {Array} [allWarehouses]
 * @property {string} [codeImportSplitter]
 * @property {string} [serialImportSplitter]
 */

/**
 * Builds import field definitions for a data source.
 * @param {string|null|undefined} dataSourceId
 * @param {BuildImportFieldsContext} context
 * @returns {import("../../../../../../components/app-specific/import-csv-file").ImportCsvFileRequiredField[]}
 */
export const buildImportFields = (dataSourceId, context = {}) => {
    const {
        allPriceLevels = [],
        lengthUnit = '',
        weightUnit = '',
        allIndustries = [],
        technicalPropertiesOfIndustry = {},
        allPartCodeCategories = [],
        allWarehouses = [],
        codeImportSplitter = DEFAULT_PRODUCT_CODE_SPLITTER,
        serialImportSplitter = ',',
        industryId,
    } = context;

        switch (dataSourceId) {
            case ImportingDataTypes.partDescription:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'description',
                        title: 'Description',
                        generator: (index) => `Sample Description ${index + 1}`,
                        format: (e) => e?.toString()?.trim()
                    }
                ];

            case ImportingDataTypes.partTitle:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'title',
                        title: 'Title',
                        generator: (index) => `Sample Product Title ${index + 1}`,
                        format: (e) => e?.toString()?.trim()
                    }
                ];

            case ImportingDataTypes.partShortDescription:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'shortDescription',
                        title: 'Short Description',
                        generator: (index) => `Sample short description ${index + 1}`,
                        format: (e) => e?.toString()?.trim()
                    }
                ];

            case ImportingDataTypes.partNotes:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'title',
                        title: 'Note Title',
                        generator: (index) => `Note ${index + 1}`,
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'message',
                        title: 'Note Message',
                        generator: (index) => `Sample note message ${index + 1}`,
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'isPrivate',
                        title: 'Is Private',
                        generator: (index) => index % 2 === 0 ? 'true' : 'false',
                        format: (e) => e?.toString()?.toLowerCase()
                    }
                ];

            case ImportingDataTypes.partUPC:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'upc',
                        title: 'UPC',
                        generator: (index) => `${(123456789000 + index).toString().padStart(12, '0')}`,
                        format: (e) => e?.toString()?.trim()
                    }
                ];

            case ImportingDataTypes.partVisibility:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => index === 0 ? "NOTE (acceptable values)" : `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'isVisible',
                        title: 'Is Visible in internal system  (if applicable)',
                        generator: (index) => index === 0 ? "Yes or No | True or False | true or false" : "",
                        format: (e) => e?.toString()?.toLowerCase(),
                        optional: true,
                    },
                    {
                        name: "isVisibleForClient",
                        title: "Is visible in customer website (if applicable)",
                        generator: (index) => index === 0 ? "Yes or No | True or False | true or false" : "",
                        format: (e) => e?.toString()?.toLowerCase(),
                        optional: true,
                    }
                ];
            case ImportingDataTypes.partPriceLevels:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    ...allPriceLevels.map(level => ({
                        name: `priceLevel_${level.id}`,
                        title: level.title,
                        optional: true,
                        generator: (index) => formatMoney(((index + 1) * 9.99).toFixed(2)),
                        format: (e) => e?.toString()?.trim()
                    }))
                ];

            case ImportingDataTypes.partSerializability:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'isSerialized',
                        title: 'Is Serialized',
                        generator: (index) => index % 2 === 0 ? 'true' : 'false',
                        format: (e) => e?.toString()?.toLowerCase(),
                    }
                ];

            case ImportingDataTypes.partDeliveryMode:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'deliveryMode',
                        title: 'Delivery Mode',
                        generator: (index) => DELIVERY_MODE_IMPORT_TITLES[index % DELIVERY_MODE_IMPORT_TITLES.length],
                        format: (e) => e?.toString()?.trim()
                    }
                ];

            case ImportingDataTypes.partOnlineOrderEligibility:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'eligibleForOnlineOrdering',
                        title: 'Eligible For Online Ordering',
                        generator: (index) => index % 2 === 0 ? 'true' : 'false',
                        format: (e) => e?.toString()?.toLowerCase(),
                    }
                ];
            case ImportingDataTypes.productVisibilityToOtherPartners:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'isPublicForBuyers',
                        title: 'Public to other partners',
                        generator: (index) => index % 2 === 0 ? 'true' : 'false',
                        format: (e) => e?.toString()?.toLowerCase(),
                    },
                ];
            case ImportingDataTypes.customersTerritoriesAndAccountManagers:
                return [
                    {
                        name: 'customerNo',
                        title: 'Customer Number',
                        generator: (index) => `EXAMPLE ${(index + 1).toString().padStart(5, '0')}`,
                        format: formatImportCodeUpperField
                    },
                    {
                        name: 'territoryCode',
                        title: 'Territory Code',
                        format: formatImportCodeUpperField,
                    },
                    {
                        name: 'accountManagerCode',
                        title: 'Account Manager Code',
                        format: formatImportCodeUpperField,
                    },
                ]
            case ImportingDataTypes.removeProduct:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                ];

            case ImportingDataTypes.productFlatShippingRates:
                return [
                    {
                        name: 'partNo',
                        title: 'Part No',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'amount',
                        title: 'Amount',
                        generator: (index) => ((index + 1) * 10).toFixed(2),
                        format: (e) => e?.toString()?.trim(),
                    },
                ];

            case ImportingDataTypes.productsFromAnotherPartner:
                return [
                    {
                        name: 'partId',
                        title: 'Part ID',
                        generator: () => '',
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'partnerConnectionId',
                        title: 'Partner Connection ID',
                        generator: () => '',
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    },
                    {
                        name: 'partnerName',
                        title: 'Partner Name',
                        generator: (index) => `Partner ${index + 1}`,
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    },
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: () => "",
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'keepUpdate',
                        title: 'Keep Update',
                        generator: (index) => (index % 2 === 0 ? 'true' : 'false'),
                        format: (e) => e?.toString()?.toLowerCase(),
                    },
                    ...allWarehouses.map((warehouse) => ({
                        name: `warehouse_${warehouse.id}`,
                        title: warehouse.code ?? warehouse.title ?? String(warehouse.id),
                        optional: true,
                        generator: () => 'False',
                        format: (e) => e?.toString()?.trim(),
                    })),
                ];

            case ImportingDataTypes.productWarehouseAssignment:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'warehouseCode',
                        title: 'Warehouse Code',
                        generator: (index) => (index === 0 ? '00' : `WH-${(index).toString().padStart(2, '0')}`),
                        format: formatImportCodeUpperField,
                    },
                ];

            case ImportingDataTypes.setInventoryAvailability:
                return [
                    {
                        name: 'warehouseCode',
                        title: 'Warehouse Code',
                        generator: (index) => (index === 0 ? '00' : `WH-${(index).toString().padStart(2, '0')}`),
                        format: formatImportCodeUpperField,
                    },
                    {
                        name: 'partNo',
                        title: 'Part No',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'quantity',
                        title: 'Quantity',
                        generator: (index) => String(index + 1),
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'avgCost',
                        title: 'Avg Cost',
                        generator: () => '0',
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'serialNumbers',
                        title: 'Serial Numbers',
                        optional: true,
                        generator: () => '',
                        format: (e) => parseDelimitedList(e, serialImportSplitter),
                    },
                ];

            case ImportingDataTypes.warehouseAlertThresholds:
                return [
                    {
                        name: 'warehouseCode',
                        title: 'Warehouse Code',
                        generator: (index) => `Whs-${(index + 1).toString().padStart(3, '0')}`,
                        format: formatImportCodeUpperField
                    },
                    {
                        name: 'partNo',
                        title: 'Part No',
                        showProductInformationPopover: true,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'reOrderPoint',
                        title: 'Re-Order Point',
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'minimumOrder',
                        title: 'Minimum Order',
                        format: (e) => e?.toString()?.trim(),
                    },
                ]
            case ImportingDataTypes.productDimensions:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'vendorCode',
                        title: 'Vendor Code',
                        generator: (index) => index % 2 === 0 ? `VEND-${(Math.floor(index / 2) + 1).toString().padStart(4, '0')}` : '',
                        format: formatImportCodeUpperField
                    },
                    {
                        name: 'length',
                        title: `Length (${lengthUnit})`,
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'width',
                        title: `Width (${lengthUnit})`,
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'height',
                        title: `Height (${lengthUnit})`,
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'weight',
                        title: `Weight (${weightUnit})`,
                        format: (e) => e?.toString()?.trim(),
                    },
                ]
            case ImportingDataTypes.productVendors:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'vendorCode',
                        title: 'Vendor Code',
                        generator: (index) => index % 2 === 0 ? `VEND-${(Math.floor(index / 2) + 1).toString().padStart(4, '0')}` : '',
                        format: formatImportCodeUpperField
                    },
                    {
                        name: 'vendorPartNumber',
                        title: 'Vendor Part Number',
                        generator: (index) => index % 2 === 0 ? `VEND-PART-${(Math.floor(index / 2) + 1).toString().padStart(5, '0')}` : '',
                        format: formatImportCodeUpperField
                    },
                    {
                        name: 'vendorPrice',
                        title: `Vendor Price`,
                        generator: (index) => formatMoney(((index + 1) * 9.99).toFixed(2)),
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'startDateTime',
                        title: 'Start Date',
                        generator: (index) => DateUtils.instance().add(index + 1, 'day').format('YYYY-MM-DD'),
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'endDateTime',
                        title: 'End Date',
                        generator: (index) => index > 5 ? '' : DateUtils.instance().add(index + 10, 'day').format('YYYY-MM-DD'),
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    },
                    {
                        name: 'id',
                        title: "Connection/Conflict ID",
                        generator: () => '',
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    },
                    {
                        name: 'conflictedWith',
                        title: "Conflicted With",
                        generator: () => '',
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    }
                ];
            case ImportingDataTypes.priceMatrices:
                return [
                    {
                        name: 'type',
                        title: 'Type',
                        generator: (index) => {
                            const types = [PriceMatrixTypes.part, PriceMatrixTypes.priceLevel, PriceMatrixTypes.category, PriceMatrixTypes.general]
                            return types[index % types.length]?.title ?? "";
                        },
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'industry',
                        title: 'Industry',
                        generator: (index) => ["Part", "Price Level", "Category", "General"][index % 4] === 'Category' ? allIndustries?.[index % allIndustries.length]?.title ?? '' : '',
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'target',
                        title: 'Target',
                        generator: (index) => ["Part", "Price Level", "Category", "General"][index % 4],
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'change',
                        title: 'Change',
                        generator: (index) => index % 2 === 0 ? '$10.00 (Fixed)' : '-10%',
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'basedOn',
                        title: 'Based on',
                        generator: () => 'Final Price',
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'startDate',
                        title: 'Start Date',
                        generator: (index) => DateUtils.instance().add(index + 1, 'day').format('YYYY-MM-DD'),
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'endDate',
                        title: 'End Date',
                        generator: (index) => index > 5 ? '' : DateUtils.instance().add(index + 10, 'day').format('YYYY-MM-DD'),
                        format: (e) => e?.toString()?.trim(),
                        optional: true,
                    },
                    {
                        name: 'customerCode',
                        title: 'Customer Code',
                        generator: () => '',
                        format: formatImportCodeUpperField,
                        optional: true,
                    }
                ];
            case ImportingDataTypes.externalCustomers:
                return [
                    {
                        name: 'customerCode',
                        title: 'Customer Code',
                        generator: (index) => `CUST-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatSpreadsheetImportField,
                    },
                    {
                        // addUser
                        name: 'addUser',
                        title: 'Add User',
                        generator: () => 'False',
                        format: (e) => e?.toString()?.toLowerCase() === 'true' ? 'True' : 'False',
                        optional: true
                    },
                    {
                        name: 'internalId',
                        title: 'Internal ID',
                        generator: (index) => `100${index + 1}`,
                        format: formatSpreadsheetImportField,
                        optional: true
                    },
                ];
            case ImportingDataTypes.externalVendors:
                return [
                    {
                        name: 'code',
                        title: 'Vendor Code',
                        generator: (index) => `V${(index + 1).toString().padStart(4, '0')}`,
                        format: formatSpreadsheetImportField,
                    },
                ];
            case ImportingDataTypes.productCategoryAssignment:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField
                    },
                    {
                        name: 'parentCategoryTitle',
                        title: 'Root Category',
                        generator: (index) => `Root Category ${(index % 3) + 1}`,
                        format: (e) => e?.toString()?.trim()
                    },
                    {
                        name: 'categoryTitle',
                        title: 'Leaf Category',
                        generator: (index) => `Leaf Category ${(index % 5) + 1}`,
                        format: (e) => e?.toString()?.trim()
                    }
                ];
            case ImportingDataTypes.productFormAssignment:
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'formTitle',
                        title: 'Form Title',
                        generator: (index) => `Sample Form ${index + 1}`,
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'isRequiredToFill',
                        title: 'Is Required to Fill',
                        generator: (index) => (index % 2 === 0 ? 'true' : 'false'),
                        format: (e) => e?.toString()?.toLowerCase(),
                    },
                ];

            case ImportingDataTypes.productRelationAssignment:
                return [
                    {
                        name: 'sourcePartNumber',
                        title: 'Source Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    {
                        name: 'relationTitle',
                        title: 'Relation Title',
                        generator: () => 'Related Product',
                        format: (e) => e?.toString()?.trim(),
                    },
                    {
                        name: 'relationPartNumber',
                        title: 'Relation Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 101).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                ];
            case ImportingDataTypes.productCodeAssignment: {
                const splitter = codeImportSplitter ?? DEFAULT_PRODUCT_CODE_SPLITTER;
                return [
                    {
                        name: 'partNumber',
                        title: 'Part Number',
                        showProductInformationPopover: true,
                        generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                        format: formatPartNumberImportField,
                    },
                    ...(allPartCodeCategories ?? []).map((category, index) => ({
                        name: codeGroupFieldKey(category.id),
                        title: category.title,
                        optional: true,
                        generator: (rowIndex) => (
                            index === 0 && rowIndex === 0
                                ? `SAMPLE-A${splitter}SAMPLE-B`
                                : `CODE-${rowIndex + 1}`
                        ),
                        format: formatPartCodeImportField,
                    })),
                ];
            }
            default:
                if (dataSourceId === ImportingDataTypes.productProperties) {
                    return [
                        {
                            name: 'title',
                            title: 'Title',
                            generator: (index) => `Sample Property ${index + 1}`,
                            format: (e) => e?.toString()?.trim()
                        },
                        {
                            name: 'description',
                            title: 'Description',
                            generator: (index) => `Sample Description ${index + 1}`,
                            format: (e) => e?.toString()?.trim(),
                            optional: true
                        },
                        {
                            name: 'type',
                            title: 'Type',
                            generator: (index) => ["Text", "Number", "Decimal", "Fraction", "Currency", "True / False", "Date", "Color"][index % 8],
                            format: (e) => e?.toString()?.trim()
                        }
                    ];
                } else if (dataSourceId === ImportingDataTypes.productPropertyOptions) {
                    return [
                        {
                            name: 'propertyTitle',
                            title: 'Property Title',
                            generator: (index) => `Sample Property ${index + 1}`,
                            format: (e) => e?.toString()?.trim()
                        },
                        {
                            name: 'hasPreDefinedValues',
                            title: 'Has PreDefined Values',
                            generator: () => 'True',
                            format: (e) => e?.toString()?.trim()
                        },
                        {
                            name: 'isMultiSelect',
                            title: 'Is Multi-Select',
                            generator: () => 'False',
                            format: (e) => e?.toString()?.trim(),
                            optional: true
                        },
                        {
                            name: 'predefinedValues',
                            title: 'Options (Comma Separated)',
                            generator: (index) => index === 0 ? `Option A, Option B, Option C` : '',
                            format: (e) => e?.toString()?.trim(),
                            optional: true
                        }
                    ];
                } else if (dataSourceId === ImportingDataTypes.productTagAssignment) {
                    return [
                        {
                            name: 'partNumber',
                            title: 'Part Number',
                            showProductInformationPopover: true,
                            generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                            format: formatPartNumberImportField,
                        },
                        {
                            name: 'tagName',
                            title: 'Tag Name',
                            generator: (index) => `Sample Tag ${index + 1}`,
                            format: (e) => e?.toString()?.trim(),
                        },
                    ];
                } else if (dataSourceId === ImportingDataTypes.partTechnicalInformation) {
                    const properties = technicalPropertiesOfIndustry[industryId] || [];
                    return [
                        {
                            name: 'partNumber',
                            title: 'Part Number',
                            showProductInformationPopover: true,
                            generator: (index) => index === 0 ? "Sample Part with Property Value Templates" : `PART-${(index + 1).toString().padStart(5, '0')}`,
                            format: formatPartNumberImportField,
                        },
                        ...properties.map(prop => {
                            let titleSuffix;
                            switch (prop.industryPropertyTypeId) {
                                case ProductTechnicalPropertyTypeIds.text:
                                    titleSuffix = "(Text)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.number:
                                    titleSuffix = "(Number)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.date:
                                    titleSuffix = "(Date)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.boolean:
                                    titleSuffix = "(True/False)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.decimal:
                                    titleSuffix = "(Decimal)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.money:
                                    titleSuffix = "(Monetary Value)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.fraction:
                                    titleSuffix = "(Fraction)";
                                    break;
                                case ProductTechnicalPropertyTypeIds.color:
                                    titleSuffix = "(Color)";
                                    break;
                                default:
                                    titleSuffix = "";
                                    break;
                            }
                            return ({
                                name: `property_${prop.id}`,
                                title: prop.title + ` ${titleSuffix}`,
                                optional: true,
                                generator: (index) => {
                                    if (index === 0) {
                                        switch (prop.industryPropertyTypeId) {
                                            case ProductTechnicalPropertyTypeIds.text:
                                                return `Text`;
                                            case ProductTechnicalPropertyTypeIds.number:
                                                return `12345`;
                                            case ProductTechnicalPropertyTypeIds.date:
                                                return `2024-01-01`;
                                            case ProductTechnicalPropertyTypeIds.boolean:
                                                return `true`;
                                            case ProductTechnicalPropertyTypeIds.decimal:
                                                return `123.45`;
                                            case ProductTechnicalPropertyTypeIds.money:
                                                return `99.99`;
                                            case ProductTechnicalPropertyTypeIds.fraction:
                                                return `3-1/2`;
                                            case ProductTechnicalPropertyTypeIds.color:
                                                return `#FF5733`;
                                            default:
                                                return "";
                                        }
                                    } else {
                                        return "";
                                    }
                                },
                                format: (e) => e?.toString()?.trim()
                            })
                        })
                    ]
                }
                if (dataSourceId === ImportingDataTypes.externalProducts) {
                    return [
                        {
                            name: 'internalId',
                            title: 'Internal ID',
                            generator: (index) => `100${index + 1}`,
                            format: formatSpreadsheetImportField,
                            optional: true
                        },
                        {
                            name: 'partNumber',
                            title: 'Part Number',
                            showProductInformationPopover: true,
                            generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                            format: formatPartNumberImportField
                        },
                    ];
                } else {
                    return [];
                }
        }
};
